import { ProductsService } from 'src/app/services/products.service';
import { environment } from 'src/environments/environment';
import { IClient } from './models/client.model';
import { DepartmentService } from './services/department.service';
import { Component, OnDestroy, OnInit, HostListener, PLATFORM_ID, Inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DEFAULT_LANGUAGE } from 'src/app/shared/utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import { filter, take, takeUntil } from 'rxjs/operators';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TokenService } from './services/token.service';
import { Subject } from 'rxjs';
import { PixelEvent } from '@models/pixel.model';
import { MarketingService } from '@services/marketing.service';
import { encryptTextSHA256 } from '@shared/encryptText';
import { AppStateUser } from './state/user/user.reducer';
import { Store } from '@ngrx/store';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HomeToolbarComponent } from '@shared/components/home-toolbar/home-toolbar.component';
import { HomeFooterComponent } from '@shared/components/home-footer/home-footer.component';
import { NewsletterComponent } from '@shared/components/newsletter/newsletter.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ICONS } from '@shared/icons';
import { MatButtonModule } from '@angular/material/button';
import { Department } from '@models/departments.model';
import { Category } from '@models/categories.model';

declare let gtag: any;
declare let pintrk: Function;
declare let fbq: Function;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    RouterOutlet,
    CommonModule,
    TranslateModule,
    MatIconModule,
    HomeToolbarComponent,
    HomeFooterComponent,
    NewsletterComponent,
    MatSidenavModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule
  ]
})
export class AppComponent implements OnInit, OnDestroy {

  public auth: boolean;
  public home: boolean;
  public club: boolean;
  public paypal: boolean;
  public loading = false;
  public purchase: boolean;
  private subs = new Subject<void>();
  public displayCategories = false;
  public params: { name: string; value: string }[];
  public client: IClient;
  public isMobile: boolean;
  public copy = false;
  public siteStatus = false;
  public categories: Category[] = [];
  public departments: Department[] = [];
  public currentDepartment: Department | null;
  public viewToolbar = true;
  title = 'AlSuper-Cliente-Frontend';
  public appStoreUrl: string = environment.appStore;
  public googlePlayUrl: string = environment.googlePlay;
  public urlStore: string;

  public password = new UntypedFormControl('', [Validators.required]);
  public banner: string;
  public visibleBanner = true;
  public categoriesLoading = false;
  public eventBusSub?: Subscription;
  private defaultStore = environment.defaultStore;


  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (isPlatformBrowser(this.platformId)) this.isMobile = window.innerWidth <= 800;
  }

  constructor(
    private router: Router,
    private location: Location,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private iconRegistry: MatIconRegistry,
    private departmentService: DepartmentService,
    @Inject(PLATFORM_ID) private platformId: string,
    private productsService: ProductsService,
    private tokenService: TokenService,
    private marketingService: MarketingService,
    private storeUser: Store<AppStateUser>
  ) {

    if (isPlatformBrowser(this.platformId)) {
      const googleAnalyticsScript = document.createElement('script');
      googleAnalyticsScript.async = true;
      googleAnalyticsScript.setAttribute('type', 'text/javascript');
      googleAnalyticsScript.setAttribute('src', `${environment.googleAnalytics.url}${environment.googleAnalytics.id}`);
      document.body.appendChild(googleAnalyticsScript);
    }

    const navEndEvents$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    );
    navEndEvents$.pipe(takeUntil(this.subs)).subscribe((event: any) => {
      if (isPlatformBrowser(this.platformId)) {
        gtag('config', environment.googleAnalytics.id, {
          'page_path': event.urlAfterRedirects
        });

        //* Pinterest tag
        pintrk('track', 'pagevisit', {
          event_id: uuidv4(),
        });
        fbq('track', 'PageView');
        this.facebookEvent();
      }
    });
    this.getClientData();

    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
    this.translate.use(DEFAULT_LANGUAGE);

    this.registerIcons();

    this.router.events.pipe(takeUntil(this.subs)).subscribe(() => {
      this.home = this.location.path() === '' || this.location.path() === '?utm_source=Ofertia&utm_medium=Reach&utm_campaign=AlsuperEngagement' ? true : false;
      this.paypal = this.location.path().includes('paypal') || this.location.path().includes('pagos/approve') || this.location.path().includes('alsuperapp');
      this.auth = this.location.path().includes('autenticacion') || this.location.path().includes('webview-help') || this.location.path().includes('webview-tyc') || this.location.path().includes('webview-aviso') || this.location.path().includes('cuenta-eliminada');
      this.purchase = this.location.path().includes('compra-en-proceso');
    });
    this.productsService.setCopyLink$.pipe(takeUntil(this.subs)).subscribe((res: boolean) => {
      if (res) {
        if (isPlatformBrowser(this.platformId)) {
          const url = window.location.href;
          this.copyText(url);
        }
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      const userAg = navigator.userAgent;
      if (userAg.toLowerCase().includes('android')) {
        this.urlStore = this.googlePlayUrl;
      } else {
        this.urlStore = this.appStoreUrl;
      }
    }
  }

  private registerIcons(): void {

    const NO_WIFI_SVG_LITERAL = `<svg width="157" height="130" viewBox="0 0 157 130" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
        d="M129.198 129.841L67.7062 68.0227C62.5819 69.222 57.812 71.0209 53.3964 73.4195C48.9807 75.8181 45.0285 78.8164 41.5396 82.4143L27.8021 68.3497C31.291 64.8609 35.0524 61.8081 39.0865 59.1914C43.1205 56.5747 47.4271 54.2852 52.0062 52.3227L37.2875 37.6039C32.8174 39.8935 28.647 42.4284 24.7766 45.2086C20.9061 47.9888 17.2264 51.1234 13.7375 54.6122L0 40.5477C3.48889 37.0588 7.11406 33.9242 10.8755 31.144C14.637 28.3638 18.6437 25.7199 22.8958 23.2122L9.15833 9.47474L18.3167 0.316406L138.683 120.683L129.198 129.841ZM78.5 119.375C73.9208 119.375 70.0503 117.767 66.8885 114.55C63.7267 111.334 62.1458 107.491 62.1458 103.021C62.1458 98.4414 63.7267 94.5709 66.8885 91.4091C70.0503 88.2473 73.9208 86.6664 78.5 86.6664C83.0792 86.6664 86.9497 88.2473 90.1115 91.4091C93.2733 94.5709 94.8542 98.4414 94.8542 103.021C94.8542 107.491 93.2733 111.334 90.1115 114.55C86.9497 117.767 83.0792 119.375 78.5 119.375ZM117.096 80.4518L112.353 75.7091L107.61 70.9664L84.0604 47.4164C92.8917 48.2886 101.151 50.5237 108.837 54.1216C116.523 57.7195 123.31 62.4622 129.198 68.3497L117.096 80.4518ZM143.262 54.6122C134.867 46.2171 125.137 39.6482 114.07 34.9055C103.004 30.1628 91.1472 27.7914 78.5 27.7914C76.2104 27.7914 74.0026 27.8732 71.8766 28.0367C69.7505 28.2003 67.5972 28.4456 65.4167 28.7727L48.7354 12.0914C53.5326 10.7831 58.4116 9.80182 63.3724 9.14766C68.3332 8.49349 73.3757 8.16641 78.5 8.16641C93.9819 8.16641 108.428 11.0556 121.839 16.8341C135.249 22.6126 146.969 30.5171 157 40.5477L143.262 54.6122Z"
        fill="#BDD3B4" />
</svg>`;

    // 1. Registrar el icono 'ic-wifi' usando addSvgIconLiteral
    this.iconRegistry.addSvgIconLiteral(
      'ic-wifi',
      this.sanitizer.bypassSecurityTrustHtml(NO_WIFI_SVG_LITERAL)
    );

    if (typeof ICONS !== 'undefined' && Object.keys(ICONS).length > 0) {
      Object.keys(ICONS).forEach(name => {
        // Evita registrar 'ic-wifi' dos veces 
        if (name === 'ic-wifi') {
          return;
        }
        this.iconRegistry.addSvgIcon(name, this.sanitizer.bypassSecurityTrustResourceUrl(`${ICONS[name as keyof typeof ICONS]}`));
      });
    }
  }

  private async copyText(val: string): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      await navigator.clipboard.writeText(val);
      this.copy = true;
      setTimeout(() => {
        this.copy = false;
      }, 2500);
    }
  }

  public closeToolbar(): void {
    this.viewToolbar = false;
  }

  public getDepartments(): void {
    const storeId = JSON.parse(localStorage.getItem('address') || '{}').store;
    this.departmentService.getDepartments(storeId ?? this.defaultStore).pipe(take(1)).subscribe(({
      next: ({ data }) => {
        this.departments = data.map((department: Department) => {
          department.show = false;
          const nameArr = department.description.split(' ');
          const desc = nameArr.join('-');
          department.department_url = `departamento/${desc.toLowerCase()}-${department.department_id}`;
          return department;
        });
      }
    }));
  }

  public async showCategories(department: Department, index: number): Promise<void> {
    this.categoriesLoading = true;
    const { data } = await this.departmentService.getCategories(department.department_id.toString());

    this.categories = data.categories;
    const nameArr = department.description.split(' ');
    const desc = nameArr.join('-');
    this.categories.forEach((category: Category) => {
      const departmentId = `${desc.toLowerCase()}-${department.department_id}`;
      category.queryParams = {
        departamento_id: departmentId,
        categoria: category.category_name,
        categoria_id: category.category_id
      };
    });
    this.departments.forEach((item: any, arrIndex: number) => {
      item.show = false;
      if (arrIndex === index) {
        this.currentDepartment = null;
        if (item.show) {
          this.currentDepartment = null;
          item.show = false;
        } else {
          this.currentDepartment = item;
          item.show = true;
        }
      }
    });
    this.categoriesLoading = false;
  }

  public getClientData(): void {
    this.storeUser.select('user').pipe(takeUntil(this.subs)).subscribe(({ data }) => {
      this.client = data;
    });
  }

  private facebookEvent() {
    try {
      if (isPlatformBrowser(this.platformId)) {
        const pixelEvent: PixelEvent = {
          data: {
            event_name: 'PageView',
            event_time: Date.now(),
            action_source: 'website',
            user_data: {
              em: encryptTextSHA256(this.client?.email) ?? '',
              ph: encryptTextSHA256(this.client?.cellphone) ?? '',
              fbp: document.cookie.match('(^|;)\\s*' + '_fbp' + '\\s*=\\s*([^;]+)')?.pop() || '',
              fbc: document.cookie.match('(^|;)\\s*' + '_fbc' + '\\s*=\\s*([^;]+)')?.pop() || '',
              external_id: encryptTextSHA256(this.client?.user_id.toString()) ?? ''
            },
          }
        };
        this.marketingService.pixelCall(pixelEvent);
      }
    } catch (error) {
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth <= 800;
    }
  }

  public logout(): void {
    this.tokenService.signOut();
  }

  @HostListener('window:scroll', ['$event'])
  public onScrollToolbarBaner($event: any): void {
    if (isPlatformBrowser(this.platformId)) {
      const verticalOffsetBanner = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      if (verticalOffsetBanner >= 5) {
        this.visibleBanner = false;
      } else if (verticalOffsetBanner <= 5) {
        this.visibleBanner = true;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.eventBusSub) {
      this.eventBusSub.unsubscribe();
    }
    this.subs.next();
    this.subs.complete();
  }
}
